import { useState, useContext, useEffect } from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'

import { DataContext, DataDispatchContext } from '../../contexts/data/dataContext'
import { useGetCurrentSession } from '../../services/api-query/auth'
import { 
  useGetDirectDebitBankInfo
} from '../../services/api-query/directDebit'

import custom from '../../styles/mui/szVariables.json'

export default function DirectDebitStep1({ onContinue }: any) {
  const data = useContext(DataContext)
  const dispatch: any = useContext(DataDispatchContext)
  const [accountName, setAccountName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [error, setError] = useState('')
  const { currentSession } = useGetCurrentSession()
  const { bankInfo } = useGetDirectDebitBankInfo(
    accountNumber?.replaceAll('-','').substring(0,2).trim(),
    accountNumber?.replaceAll('-','').substring(2,6).trim(),
    currentSession
  )

  let desc = data?.debitForm?.formDescription

  if(!data?.customer?.bankDirectDebit) {
    desc = data?.debitForm?.formDescription.replace(' or update','')
  }

  const acctNameRegex = new RegExp(/^[a-zA-Z0-9.,:'()+?&\-\/ ]*$/)
  const handleAccountName = (e: any) => {
    if(`${e.target.value}`.match(acctNameRegex)) {
      setAccountName(e.target.value)
    }
    
  }

  
  const checkFormat = (val: string) => {
    let newVal = val.replaceAll('-','')  ;
    if(newVal.length>2 && newVal[2]!=='-') 
      newVal = newVal.slice(0,2)+'-'+newVal.slice(2)
    if(newVal.length>7 && newVal[7]!=='-') 
      newVal = newVal.slice(0,7)+'-'+newVal.slice(7)  
    if(newVal.length>15 && newVal[15]!=='-') 
      newVal = newVal.slice(0,15)+'-'+newVal.slice(15)
    for(let i=0;i<newVal.length;i++) {
      if(newVal[i]==='-' && i!==2 && i!==7 && i!==15){
        newVal = newVal.slice(0,i)+newVal.slice(i+1)
      }
    }
    return newVal.replaceAll('--','-')  
  }

  const validate = (val: string)=>{
    if(val.length>7 && !bankInfo) {
      setError('Invalid account number. Please enter a valid NZ bank account number with 15 or 16 digits')
    } else {
      setError('')
    }
  }


  const handleAccountNumber = (e: any, format=false) => {
    setError('')
    let val = e.target.value
    if(val.length>2 && format) {
      val = checkFormat(val)
    }
    val = val.slice(0,19).replace(/[^0-9-]/g,'')
    setAccountNumber(val)
    validate(val)
  }

  function handleDebitBankAccount(){
    dispatch({
      type: 'setDirectDebitBank',
      directDebitBank: {
        accountNumber,
        accountName,
        error: null
      }
    })
    onContinue()
  }

 const isValid =  accountName.trim() !== '' && 
                  accountNumber.trim() !== '' && 
                  accountNumber.length>17 &&
                  bankInfo

  function handleEnterKey(ev: any) {
    if(ev.key==='Backspace') return
    if(ev.key === "Enter" && isValid) {
      handleDebitBankAccount()
    }
  }

  return (
      <Box 
        display="flex"
        flexDirection="column" 
        justifyContent="center" 
        alignItems="flex-left" 
        maxWidth={"100%"}
        p={custom.spacing.md}>
          <Typography textAlign="left" 
              maxWidth={"100%"}
              id="formDescription"
              variant="szBody.Regular.Medium">
              {desc}
          </Typography>
          {data?.customer?.bankDirectDebit && 
            <Stack
              direction="row"
              maxWidth={"100%"}
              mt={custom.spacing.sm}
              p={custom.spacing.sm}
              sx={{
                border: custom.selected,
                borderRadius: custom.corners5,
                backgroundColor: 'rgba(0, 111, 244, 0.05)',
                alignItems: 'flex-start',
              }}>
              <Box
                component={'img'}
                id="Information Icon"
                ml="auto"
                mr={custom.spacing.xxxs}
                alt="info icon"
                src="/images/InfoIcon2.svg"
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography textAlign="left" 
                id="infoText"
                variant="szBody.Regular.Medium">
                {data?.debitForm?.infoText}
                <span style={{ color: 'primary.main' }}>
                  <a href="tel:0800116655" id="phoneNumber"
                      style={{ textDecoration: 'none'}}>
                          {data?.debitForm?.solarZeroPhoneNumber}
                  </a>
                </span>
              </Typography>
            </Stack>
          }
          <Typography mt={custom.spacing.sm} 
              variant="szHeading.Bold.xSmall">
            Account number
            <Typography 
              ml={custom.spacing.sm}
              variant="szBody.Regular.xSmall"
              color="text.tertiary">
              {accountNumber?.length>1?'(xx-xxxx-xxxxxxx-xxx)':''}
            </Typography>
          </Typography>
          <Box mt={custom.spacing.xxxs}>
            <TextField
              fullWidth
              id="accountNumber"
              InputProps={{ 
                sx: { 
                  borderRadius: custom.corners3 
                } 
              }}
              placeholder="xx-xxxx-xxxxxxx-xxx"
              value={accountNumber}
              onBlur={(e:any)=>handleAccountNumber(e,true)}
              onKeyUp={handleEnterKey}
              onChange={handleAccountNumber}
            />
          </Box>
          <Typography 
              my={custom.spacing.xxxxs}
              ml={custom.spacing.xxxs}
              color={"error"}
              id="acctNumberError" 
              variant="szBody.Regular.xSmall">
                {error}
          </Typography>
          <Typography 
              my={custom.spacing.xxxxs}
              ml={custom.spacing.xxxs}
              color={"text.primary"} 
              variant="szBody.Regular.xSmall">
                {bankInfo?.fullName} {bankInfo?.city}
          </Typography>
          <Typography mt={custom.spacing.sm} 
              variant="szHeading.Bold.xSmall">
            Account name
          </Typography>
          <Box mt={custom.spacing.xxxs}>
            <TextField
              fullWidth
              id="accountName"
              InputProps={{ 
                sx: { 
                  borderRadius: custom.corners3 
                } 
              }}
              onKeyUp={handleEnterKey}
              value={accountName}
              onChange={handleAccountName}
            />
          </Box>
          <Box maxWidth="100%" mt={custom.spacing.lg}>
              <Button
                sx={{ p: custom.spacing.sm }}
                id="continueDirectDebit"
                color="primary"
                fullWidth
                disabled={!isValid}
                variant="contained"
                onClick={handleDebitBankAccount}>
                <Typography textAlign="center" 
                      color="#FFF"
                      variant="szBody.Regular.Medium">
                    Continue
                </Typography>
              </Button>
        </Box>
      </Box>
  )
}
